import React, { useState, useEffect, useCallback } from 'react';
import { Fade, TableRow, TableCell, alpha, Typography } from '@mui/material';
import { TrendingUp as SellIcon } from '@mui/icons-material';
import { Chip } from '@mui/material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2, fmtDate } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, SellingRow } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { fmtBD } from '../../components/common/bdNumber';

const SELL_ACCENT = '#4CAF50';

const SellingInfoReport: React.FC = () => {
  const [rows, setRows]     = useState<SellingRow[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts]     = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [sellers, setSellers]       = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({
    date_from: '', date_to: '', product_id: '', category_id: '', seller_info_id: '', sell_type: '',
  });

  useEffect(() => {
    tradesService.listProducts({ is_active: true }).then(r => setProducts(r.results.map(p => ({ value: p.product_id, label: p.product_name })))).catch(() => {});
    tradesService.listCategories({ is_active: true }).then(r => setCategories(r.results.map(c => ({ value: c.category_id, label: c.category_name })))).catch(() => {});
    tradesService.listSellers().then(r => setSellers(r.results.map(s => ({ value: s.seller_info_id, label: s.seller_name })))).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await reportService.getSellingReport(p);
      setRows(res.results.map((r, i) => ({ ...r, sl: i + 1,_qty: qtyLitre(r.quantity_pcs, r.total_litre), }))); 
      setTotals((res.totals || {}) as any);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return reportService.downloadSelling(fmt, p);
  };

  const fmtLitre = (v: number | string | null | undefined): string => {
    const n = Number(v ?? 0);
    return Number.isInteger(n) ? fmtBD(n, 0) : fmtBD(n, 1);
  };
  
  const qtyLitre = (pcs: any, litre: any): string =>
    `${fmt2(pcs ?? 0)} Pcs / ${fmtLitre(litre)} L`;

  const qtyLitreSum = (pcs: any, litre: any): string =>
    `${fmt2(pcs ?? 0)} Pcs \n${fmtLitre(litre)} L`;

  const typeColors: Record<string, any> = {
    Retail:    { bg: alpha('#2196F3', 0.1), color: '#2196F3' },
    Wholesale: { bg: alpha('#9C27B0', 0.1), color: '#9C27B0' },
  };

  const columns: ReportColumn[] = [
    { key: 'sl', label: '#', align: 'center' as const, width: 40 },
    { key: 'sale_date',         label: 'Date',             format: v => fmtDate(v), noWrap: true },
    { key: 'seller_name',       label: 'Seller Name',      format: v => v || '—', width: 150 },
    { key: 'product_name',      label: 'Product Name',     width: 180 },
    { key: 'purchase_rate',     label: 'Purchase Rate (Tk.)', align: 'right', format: v => fmt2(v) },
    { key: 'category_name',     label: 'Category',         format: v => v || '—' },
    { key: '_qty', label: 'Quantity', align: 'right', width: 150, format: v => <strong>{v}</strong> },
    //{ key: 'quantity_pcs',      label: 'Quantity',         align: 'right', format: v => <strong>{v}</strong> },
    { key: 'sell_rate',         label: 'Sell Rate (Tk.)',    align: 'right', format: v => fmt2(v) },
    // Discount column — NEW
    { key: 'discount_amount',   label: 'Discount (Tk.)',     align: 'right', format: v => <Typography fontSize="0.8rem" color="#9C27B0" fontWeight={600}>{Number(v) > 0 ? fmt2(v) : '—'}</Typography> },
    { key: 'total_sales_price', label: 'Total Sell (Tk.)',   align: 'right', format: v => <strong>{fmt2(v)}</strong> },
    { key: 'cash_amount',       label: 'Cash (Tk.)',         align: 'right', format: v => <Typography fontSize="0.8rem" color="#FF9800" fontWeight={600}>{fmt2(v)}</Typography> },
    { key: 'due_amount',        label: 'Due (Tk.)',          align: 'right', format: v => <Typography fontSize="0.8rem" color="#f44336" fontWeight={600}>{fmt2(v)}</Typography> },
    { key: 'sell_type',         label: 'Sell Type',        format: v => { const tc = typeColors[v] || {}; return <Chip size="small" label={v} sx={{ background: tc.bg, color: tc.color, fontWeight: 700, height: 20, fontSize: '0.7rem' }} />; } },
    { key: 'created_by',        label: 'Created By',       format: v => <Typography variant="caption" color="text.secondary">{v}</Typography> },
  ];

  const summaryCards: SummaryCard[] = [
    { label: 'Records',            value: rows.length,                         color: SELL_ACCENT },
    { label: 'Total Quantity', value: qtyLitreSum(totals.total_qty, totals.total_litre), color: '#2196F3' },
    { label: 'Total Purchases (Tk.)',value: fmt2(totals.total_purchase ?? 0),    color: '#00BCD4' },
    { label: 'Total Sales (Tk.)',    value: fmt2(totals.total_sales ?? 0),       color: SELL_ACCENT },
    { label: 'Total Cash (Tk.)',     value: fmt2(totals.total_cash ?? 0),        color: '#FF9800' },
    { label: 'Total Due (Tk.)',      value: fmt2(totals.total_due ?? 0),         color: '#f44336' },
    { label: 'Profit/Loss (Tk.)',    value: fmt2(totals.total_profit ?? 0),      color: '#9C27B0' },
  ];

  const totalsRow = rows.length > 0 && !loading ? (
    <TableRow sx={{ background: alpha(SELL_ACCENT, 0.06) }}>
      <TableCell colSpan={6} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>TOTALS</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#2196F3' }}>{qtyLitre(totals.total_qty, totals.total_litre)}</TableCell>
      <TableCell />
      {/* skip discount in totals row — not summed */}
      <TableCell />
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: SELL_ACCENT }}>{fmt2(totals.total_sales ?? 0)}</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{fmt2(totals.total_cash ?? 0)}</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#f44336' }}>{fmt2(totals.total_due ?? 0)}</TableCell>
      <TableCell colSpan={2} />
    </TableRow>
  ) : undefined;

  return (
    <Fade in timeout={400}><div>
      <ReportLayout title="Selling Information Report" subtitle="Sales records with profit/loss analysis"
        accentColor={SELL_ACCENT} columns={columns} rows={rows} loading={loading}
        tableMinWidth={1500}
        summaryCards={summaryCards} onRefresh={fetchData} onDownload={handleDownload}
        totalsRow={totalsRow} emptyIcon={<SellIcon sx={{ fontSize: 40 }} />}
        filters={
          <ReportFilterBar accentColor={SELL_ACCENT} values={filters}
            onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
            onClear={() => setFilters({ date_from: '', date_to: '', product_id: '', category_id: '', seller_info_id: '', sell_type: '' })}
            fields={[
              { type: 'date',   key: 'date_from',      label: 'From' },
              { type: 'date',   key: 'date_to',        label: 'To' },
              { type: 'select', key: 'product_id',     label: 'Product',   options: products,   minWidth: 200 },
              { type: 'select', key: 'category_id',    label: 'Category',  options: categories, minWidth: 160 },
              { type: 'select', key: 'seller_info_id', label: 'Seller',    options: sellers,    minWidth: 180 },
              { type: 'select', key: 'sell_type',      label: 'Sell Type', options: [{ value: 'Retail', label: 'Retail' }, { value: 'Wholesale', label: 'Wholesale' }], minWidth: 130 },
            ]}
          />
        }
      />
    </div></Fade>
  );
};
export default SellingInfoReport;